<script setup lang="ts">
import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { DialogClose, DialogContent, DialogDescription, DialogPortal, DialogRoot, DialogTitle, DialogTrigger } from 'reka-ui';

interface Props {
  heading?: string | null;
  description?: string | null;
  alignment?: 'left' | 'center' | 'right';
  to?: string | null;
  contentWidth?: 'fit' | 'md' | 'full';
  padding?: 'none' | 'md';
  topLeftPresentationImage?: string | null;
  hasOverlay?: boolean;
  hasOverflow?: boolean;
  closeButtonVariant?: 'subtle' | 'filled';
  showCloseButton?: boolean;
  // blocks Esc / click-outside dismissal — close only via DialogClose or v-model.
  preventClose?: boolean;
  // keeps the close button visible but inert (e.g. while an async action is in flight),
  // so the layout doesn't shift; pair with `preventClose` to fully lock the dialog.
  closeDisabled?: boolean;
  // visually hidden — for when another full-screen layer is shown on top of it.
  invisible?: boolean;
  // renders the `action-bottom` / `action-bottom-left` slots in a sticky footer
  // outside the scroll area, and drops the dialog's bottom padding so the footer
  // butts directly against the scroll wrapper.
  stickyFooter?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  heading: null,
  description: null,
  headingAndDescriptionCentered: false,
  to: null,
  contentWidth: 'full',
  topLeftPresentationImage: null,
  alignment: 'left',
  padding: 'md',
  hasOverlay: false,
  hasOverflow: true,
  closeButtonVariant: 'subtle',
  showCloseButton: true,
  preventClose: false,
  closeDisabled: false,
  invisible: false,
  stickyFooter: false,
});

const open = defineModel<boolean>({ default: false });

const contentClassList = computed(() => {
  return twMerge(
    cva(
      'fixed top-1/2 left-1/2 z-100 flex max-h-[85vh] grow -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-start rounded-3xl bg-white shadow-lg focus:outline-none @3xs/mobile-viewport:min-h-[65vh]',
      {
        variants: {
          width: {
            fit: 'w-max max-w-[min(95vw,400px)]',
            md: 'w-[90vw] max-w-160',
            full: 'w-[90vw] max-w-240',
          },
          padding: {
            none: '',
            md: 'px-6 py-8',
          },
          stickyFooter: {
            true: 'px-6 pt-8 pb-0',
            false: '',
          },
          hasOverflow: {
            true: '',
            false: 'overflow-hidden',
          },
          invisible: {
            true: 'invisible',
            false: '',
          },
        },
      },
    )({
      width: props.contentWidth,
      padding: props.padding,
      stickyFooter: props.stickyFooter,
      hasOverflow: props.hasOverflow,
      invisible: props.invisible,
    }),
  );
});

// The scroll box clips horizontally, so full-width children sit flush against it and
// their focus rings (drawn outside the border box) get sliced off at the sides. Where
// the dialog has padding to borrow from, inset the box: children keep their exact
// position while the clip box gains room for the ring. Full-bleed (`padding="none"`)
// dialogs have nothing to borrow, so they stay as they are.
const scrollAreaClassList = computed(() => {
  return twMerge(
    cva('self-stretch grow flex flex-col overflow-y-auto overflow-x-hidden scrollbar-hidden min-h-0', {
      variants: {
        inset: {
          true: '-mx-2 px-2',
          false: '',
        },
      },
    })({ inset: props.padding !== 'none' || props.stickyFooter }),
  );
});

const alignment = computed(() => {
  return twMerge(
    cva('', {
      variants: {
        alignment: {
          left: 'text-left',
          center: 'text-center',
          right: 'text-right',
        },
      },
    })({ alignment: props.alignment }),
  );
});

const closeButtonClasses = computed(() => {
  return twMerge(
    cva('absolute z-10 cursor-pointer appearance-none inline-flex items-center justify-center rounded-full', {
      variants: {
        closeButtonVariant: {
          subtle: 'top-4 right-4 h-4 w-4',
          filled: 'top-4 right-4 h-10 w-10 bg-primary text-primary-foreground',
        },
        closeDisabled: {
          true: 'pointer-events-none cursor-not-allowed opacity-50',
          false: '',
        },
      },
    })({ closeButtonVariant: props.closeButtonVariant, closeDisabled: props.closeDisabled }),
  );
});

const closeIconSize = computed(() => {
  return props.closeButtonVariant === 'filled' ? 'lg' : 'sm';
});

const onDismissAttempt = (event: Event) => {
  if (props.preventClose) {
    event.preventDefault();
  }
};
</script>

<template>
  <DialogRoot v-model:open="open">
    <DialogTrigger as-child>
      <slot name="trigger" />
    </DialogTrigger>
    <Teleport v-if="hasOverlay" to="body">
      <Transition
        enter-from-class="opacity-0"
        enter-active-class="transition-opacity duration-300 ease-out"
        leave-active-class="transition-opacity duration-200 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open && !invisible"
          class="fixed inset-0 z-99 bg-primary/60"
          data-testid="dialog-overlay"
        />
      </Transition>
    </Teleport>
    <DialogPortal :to="to ? to : '#teleports'">
      <DialogContent
        v-bind="$attrs"
        data-theme="anyway"
        :class="contentClassList"
        @escape-key-down="onDismissAttempt"
        @interact-outside="onDismissAttempt"
      >
        <div :class="scrollAreaClassList">
          <div class="w-full *:mb-4">
            <DialogTitle v-if="heading" as-child>
              <Heading
                size="xl"
                as="h2"
                :class="alignment"
                class="px-4 tracking-tight leading-none"
              >
                {{ heading }}
              </Heading>
            </DialogTitle>
            <DialogDescription v-if="description" as-child>
              <Text size="lg" :class="alignment">
                {{ description }}
              </Text>
            </DialogDescription>
          </div>
          <slot name="action-top" />
          <slot />
        </div>
        <div
          v-if="stickyFooter"
          class="self-stretch shrink-0 -mx-6 px-6 py-4 border-t border-border flex items-center justify-between gap-4"
        >
          <div class="min-w-0">
            <slot name="action-bottom-left" />
          </div>
          <DialogClose as-child>
            <slot name="action-bottom" />
          </DialogClose>
        </div>
        <DialogClose
          v-if="showCloseButton"
          :disabled="closeDisabled"
          :class="closeButtonClasses"
          aria-label="Close"
        >
          <Icon :name="ICONS.cancel" :size="closeIconSize" />
        </DialogClose>
        <div v-if="topLeftPresentationImage" class="absolute -top-10 -left-6">
          <NuxtImg :src="topLeftPresentationImage" class="size-24 object-scale-down" />
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
